import { AfterViewInit, ChangeDetectionStrategy, Component,
  ComponentFactoryResolver, ComponentRef, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { WidgetsAbstract } from 'src/app/abstract/widgets/widgets.abstract';
import { Logger } from 'src/app/core/logger';
import { WidgetsConfigController, WidgetsTypeEnum } from 'src/app/enums/widgets/widgets-type.enum';
import { WidgetsFormModel } from 'src/app/models/widgets/widgets-form.model';
import { WidgetsQuestionModel } from 'src/app/models/widgets/widgets-question.model';
import { WidgetsRowModel } from 'src/app/models/widgets/widgets-row.model';
import { WidgetsSectionModel } from 'src/app/models/widgets/widgets-section.model';
import { WidgetsMetadataService } from 'src/app/services/widgets/widgets-metadata.service';
import { MetadataFormComponent } from '../metadata-form/metadata-form.component';
import { MetadataQuestionComponent } from '../metadata-question/metadata-question.component';
import { MetadataSectionComponent } from '../metadata-section/metadata-section.component';

@Component({
  selector: 'app-metadata-area',
  templateUrl: './metadata-container.component.html',
  styleUrls: ['./metadata-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MetadataContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('id', { read: ViewContainerRef }) view!: ViewContainerRef;
  @Input() data: WidgetsAbstract = new WidgetsRowModel();
  @Input() isMetadataView: boolean = false;
  private maxSubscription?: Subscription;
  private componentRef?: any;

  constructor(
    private widgetsInteractionService: WidgetsMetadataService,
    private viewContainerRef: ViewContainerRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private logger: Logger) {
    this.data.name = '';
  }

  ngOnInit(): void {
    this.viewContainerRef.clear();
  }

  ngAfterViewInit() {
    // this.logger.log("after view init");
    // const componentRef = this.view.createComponent(MetadataQuestionComponent);
    // this.associateTo(componentRef);

    // If we don't receive an Observable, just show last data available
    const oldData = this.widgetsInteractionService.getMetadata();
    if (oldData) {
      // this.logger.log("MCC Show old data");
      this.showMetadataComponent(oldData);
    }

    // Later we'll show whatever data we receive
    // this.logger.log("MCC subscribe");
    this.maxSubscription = this.widgetsInteractionService.widgetMaximizedAnnounced$.subscribe(
      data => {
        const viewContainerRef = this.view;
        viewContainerRef.clear();
        this.showMetadataComponent(data);
      });
  }

  showMetadataComponent(data: WidgetsAbstract) {
    if (this.componentRef) {
      (this.componentRef as ComponentRef<any>).destroy();
    }

    if (data && data.widgetType === WidgetsTypeEnum.FORM) {
      this.componentRef = this.view.createComponent(MetadataFormComponent);
      // WidgetsAbstract data to show in the MetadataView
      this.componentRef.instance.data = data as WidgetsFormModel;
      // this.logger.log("MCC Form selected: "+this.componentRef.instance.data.data?.name);
      this.componentRef.instance.data.isEdit = false;    // Never edit Form data from the metadata area
      this.data = this.componentRef.instance.data;
      this.componentRef.instance.cd.detectChanges();
    } else if (data && data.widgetType === WidgetsTypeEnum.SECTION) {
      this.componentRef = this.view.createComponent(MetadataSectionComponent);
      this.componentRef.instance.data = data as WidgetsSectionModel;
      this.componentRef.instance.isEdit = true;
      this.componentRef.instance.isShownFromWidgetArea = false;
      this.data = this.componentRef.instance.data;
      // TODO: Not automatically refreshing...
      // @See: https://stackoverflow.com/questions/45968187/angular-view-not-updated-following-changes-to-onpush-component-created-through-v
      this.componentRef.instance.cd.detectChanges();
    } else if (data && data.widgetType === WidgetsTypeEnum.QUESTION) {
      this.componentRef = this.view.createComponent(MetadataQuestionComponent);
      const layoutData = data as WidgetsQuestionModel;
      // Option 1: Don't directly modify the question, use a copy for changes
      // const tempData = new WidgetsQuestionModel();
      // tempData.id = layoutData.id;
      // tempData.cloneFrom(layoutData);
      // this.componentRef.instance.data = tempData;
      // Option 2: Directly modify the question
      this.componentRef.instance.data = layoutData;
      this.componentRef.instance.isEdit = layoutData.isEdit;
      this.componentRef.instance.isMetadataView = this.isMetadataView;
      this.componentRef.instance.isShownFromWidgetArea = false;
      // this.logger.log("MCC Question selected: "+this.componentRef.instance.data.name);
      this.data = this.componentRef.instance.data;
      this.componentRef.instance.cd.detectChanges();
    // } else if (data && data.widgetType == WidgetsTypeEnum.RADIO) {
    //   this.componentRef = this.view.createComponent(MetadataRadioComponent);
    //   this.componentRef.instance.data = data as WidgetsRadioModel;
    //   this.data = this.componentRef.instance.data;
    //   this.componentRef.instance.cd.detectChanges();
    // } else if (data && data.widgetType == WidgetsTypeEnum.ROW) {
    } else {
      try {
        const metadata = WidgetsConfigController.get(data.widgetType)?.metadata;
        if (metadata) {
            this.componentRef = this.view.createComponent(metadata);
            this.componentRef.instance.data = data;
            this.componentRef.instance.isEdit = true;
            this.data = this.componentRef.instance.data;
            this.componentRef.instance.cd.detectChanges();  
        }
      } catch(error) {
        this.logger.warn("MetadataContainerComponent ERROR="+error.toString());
      }
    }
  }

  ngOnDestroy() {
    if (this.maxSubscription) {
      // this.logger.log("MCC unsubscribe");
      this.maxSubscription.unsubscribe();
    }

    if (this.componentRef) {
      (this.componentRef as ComponentRef<any>).destroy();
    }
  }

  associateTo(componentRef) {
    // this.logger.log("associate question");
    try {
      const question = new WidgetsQuestionModel();
      question.name = "Sample Dynamic question";
      question.seq = 66;

      // We don't want to visualize the tree here; just a single node
      // TODO: For other containers: force children to empty?
      question.children = [];  // @Warning
      componentRef.instance.data = question;
      componentRef.instance.data.labelOverrides = new Map<string, string>()
        .set("$construction", "building")
        .set("$material", "Concrete");

      // TODO: For now WE force the data
      this.data = componentRef.instance.data;
    } catch (err) {
      this.logger.warn(err);
    }
  }

  onDebug() {
    this.logger.log('onDebug');
  }
}
